import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { PageHeader } from "@/components/common/Primitives";
import { DataTable } from "@/components/common/DataTable";
import { BarcodeScanner } from "@/components/wawi/BarcodeScanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtEUR } from "@/lib/format";
import { Warehouse as WarehouseIcon } from "lucide-react";
import { Plus, Search, ScanLine, Loader2, Trash2 } from "lucide-react";

const EMPTY = { name: "", article_number: "", sku: "", ean: "", manufacturer: "", brand: "",
  category: "", purchase_price: 0, sale_price: 0, vat: 19, unit: "Stück", min_stock: 0,
  article_type: "standard", imei_required: false, serial_required: false, variants: [] };

export default function WawiArticles() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [scan, setScan] = useState(false);
  const [form, setForm] = useState(null);

  const { data, isLoading } = useQuery({ queryKey: ["wawi-articles", q], queryFn: async () => (await api.get(`/wawi/articles?q=${encodeURIComponent(q)}`)).data });
  const { data: catData } = useQuery({ queryKey: ["wawi-categories"], queryFn: async () => (await api.get("/wawi/categories")).data });
  const categories = catData?.items || [];

  const columns = [
    { key: "name", header: "Artikel", render: (a) => (
      <div className="min-w-0"><div className="truncate font-medium">{a.name}</div>
        <div className="truncate text-xs text-muted-foreground font-mono">{a.article_number} · {a.ean || "ohne EAN"}</div></div>
    ) },
    { key: "manufacturer", header: "Hersteller", mobileHidden: true, render: (a) => a.manufacturer || "—" },
    { key: "type", header: "Typ", mobileHidden: true, render: (a) => a.imei_required ? <Badge variant="secondary">IMEI</Badge> : a.serial_required ? <Badge variant="secondary">SN</Badge> : <Badge variant="outline">Standard</Badge> },
    { key: "variants", header: "Var.", render: (a) => (a.variants || []).length },
    { key: "sale_price", header: "VK", render: (a) => fmtEUR(a.sale_price) },
    { key: "stock", header: "Bestand", render: (a) => (
      <Button variant="ghost" size="sm" className="h-7 gap-1 px-2" data-testid={`article-stock-${a.id}`}
        onClick={(e) => { e.stopPropagation(); openStock(a); }}>
        <WarehouseIcon className="h-3.5 w-3.5" /><span className="text-xs">anzeigen</span></Button>
    ) },
  ];

  const [stock, setStock] = useState(null);
  const openStock = async (a) => {
    try { const r = await api.get(`/wawi/articles/${a.id}/stock`); setStock({ article: a, ...r.data }); }
    catch (err) { toast.error(apiError(err)); }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, purchase_price: Number(form.purchase_price) || 0, sale_price: Number(form.sale_price) || 0, vat: Number(form.vat) || 0, min_stock: Number(form.min_stock) || 0 };
      if (form.id) await api.put(`/wawi/articles/${form.id}`, payload);
      else await api.post("/wawi/articles", payload);
      toast.success("Artikel gespeichert"); setForm(null); qc.invalidateQueries({ queryKey: ["wawi-articles"] });
    } catch (err) { toast.error(apiError(err)); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div>
      <PageHeader title="Artikel" description={`${data?.total ?? 0} Artikel im Sortiment`}
        actions={can(user?.role, "wawi:write") && (
          <Button className="gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => setForm(EMPTY)} data-testid="new-article-button"><Plus className="h-4 w-4" />Neuer Artikel</Button>
        )} />
      <div className="mb-4 flex gap-2">
        <div className="relative flex-1 sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, Nr., SKU, EAN…" className="pl-9" data-testid="article-search-input" />
        </div>
        <Button variant="outline" onClick={() => setScan(true)} data-testid="article-scan-button"><ScanLine className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Scannen</span></Button>
      </div>
      <DataTable columns={columns} rows={data?.items} loading={isLoading} testIdPrefix="article-row"
        onRowClick={can(user?.role, "wawi:write") ? (a) => setForm({ ...EMPTY, ...a }) : undefined} />

      <BarcodeScanner open={scan} onOpenChange={setScan} title="Artikel per EAN finden" onResult={(v) => { setQ(v); setScan(false); toast.info(`Gescannt: ${v}`); }} />

      <Dialog open={!!stock} onOpenChange={(o) => !o && setStock(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Bestände · {stock?.article?.name}</DialogTitle></DialogHeader>
          {stock && (stock.items.length === 0 ? <p className="text-sm text-muted-foreground">Kein mengenbasierter Bestand erfasst (ggf. serialisierte Geräte unter „Geräte & IMEI").</p> : (
            <div className="space-y-1">
              <div className="grid grid-cols-[1fr_1fr_auto] gap-2 border-b pb-1 text-xs font-medium text-muted-foreground"><span>Variante</span><span>Lager</span><span className="text-right">Menge</span></div>
              {stock.items.map((s, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 text-sm" data-testid={`stock-breakdown-${i}`}>
                  <span className="truncate">{s.variant_name}</span>
                  <span className="truncate text-muted-foreground">{s.warehouse_name}</span>
                  <span className="text-right font-medium">{s.quantity}</span>
                </div>
              ))}
              <div className="mt-2 flex justify-between border-t pt-2 text-sm font-semibold">
                <span>Gesamt{stock.min_stock ? ` (Min: ${stock.min_stock})` : ""}</span>
                <span className={stock.min_stock && stock.total < stock.min_stock ? "text-amber-600" : ""}>{stock.total}</span>
              </div>
            </div>
          ))}
        </DialogContent>
      </Dialog>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>{form?.id ? "Artikel bearbeiten" : "Neuer Artikel"}</DialogTitle></DialogHeader>
          {form && <form onSubmit={save} className="space-y-3">
            <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="article-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>EAN/GTIN</Label><Input value={form.ean} onChange={(e) => set("ean", e.target.value)} inputMode="numeric" data-testid="article-ean-input" /></div>
              <div className="space-y-1.5"><Label>SKU</Label><Input value={form.sku} onChange={(e) => set("sku", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Hersteller</Label><Input value={form.manufacturer} onChange={(e) => set("manufacturer", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Kategorie</Label>
                <Select value={form.category || "none"} onValueChange={(v) => set("category", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="article-category"><SelectValue placeholder="Keine" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Keine</SelectItem>
                    {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Artikeltyp</Label>
                <Select value={form.article_type || "standard"} onValueChange={(v) => set("article_type", v)}>
                  <SelectTrigger data-testid="article-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="geraet">Gerät (Smartphone, serialisiert)</SelectItem>
                    <SelectItem value="zubehoer">Zubehör (mengenbasiert)</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                  </SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>EK-Preis (€)</Label><Input type="number" step="0.01" value={form.purchase_price} onChange={(e) => set("purchase_price", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>VK-Preis (€)</Label><Input type="number" step="0.01" value={form.sale_price} onChange={(e) => set("sale_price", e.target.value)} data-testid="article-price-input" /></div>
              <div className="space-y-1.5"><Label>MwSt (%)</Label><Input type="number" value={form.vat} onChange={(e) => set("vat", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Mindestbestand</Label><Input type="number" value={form.min_stock} onChange={(e) => set("min_stock", e.target.value)} /></div>
            </div>
            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
              <span className="text-sm">IMEI-pflichtig (Smartphone)</span>
              <Switch checked={form.imei_required} onCheckedChange={(v) => set("imei_required", v)} data-testid="article-imei-switch" />
            </div>
            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
              <span className="text-sm">Seriennummernpflichtig</span>
              <Switch checked={form.serial_required} onCheckedChange={(v) => set("serial_required", v)} />
            </div>
            <div className="space-y-2 rounded-md border border-border p-3">
              <div className="flex items-center justify-between">
                <Label>Varianten (Farbe / Speicher)</Label>
                <Button type="button" size="sm" variant="outline" data-testid="add-variant-button"
                  onClick={() => set("variants", [...(form.variants || []), { name: "", sku: "", ean: "" }])}>+ Variante</Button>
              </div>
              {(form.variants || []).length === 0 && <p className="text-xs text-muted-foreground">Keine Varianten — Bestand wird auf Artikelebene geführt.</p>}
              {(form.variants || []).map((v, i) => (
                <div key={i} className="flex gap-2" data-testid={`variant-row-${i}`}>
                  <Input placeholder="z. B. Schwarz 128 GB" value={v.name} data-testid={`variant-name-${i}`}
                    onChange={(e) => { const vs = [...form.variants]; vs[i] = { ...vs[i], name: e.target.value }; set("variants", vs); }} />
                  <Input placeholder="EAN" value={v.ean || ""} className="w-32"
                    onChange={(e) => { const vs = [...form.variants]; vs[i] = { ...vs[i], ean: e.target.value }; set("variants", vs); }} />
                  <Button type="button" variant="ghost" size="icon" onClick={() => set("variants", form.variants.filter((_, x) => x !== i))}>
                    <Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setForm(null)}>Abbrechen</Button>
              <Button type="submit" className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="article-save-button">Speichern</Button>
            </DialogFooter>
          </form>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
